import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, User, AlertCircle, CheckCircle } from 'lucide-react';
import { cookieManager } from '../../utils/cookieManager.js';
import { getApiBaseUrl } from '../../utils/apiConfig.js';
import Footer from '../shared/Footer';

const API_BASE_URL = getApiBaseUrl();

// 安全工具函数
const sanitizeInput = (input) => {
  // 移除潜在的恶意字符和脚本
  return input
    .replace(/[<>]/g, '') // 移除尖括号
    .replace(/javascript:/gi, '') // 移除javascript协议
    .replace(/on\w+=/gi, '') // 移除事件处理器
    .trim();
};

const validateUsername = (username) => {
  // 用户名验证 - 只要求不为空且长度合理
  const errors = [];
  if (username.length === 0) {
    errors.push('用户名不能为空');
  }
  if (username.length > 50) {
    errors.push('用户名不能超过50个字符');
  }
  return {
    isValid: errors.length === 0,
    errors
  };
};

const validatePassword = (password) => {
  // 密码验证 - 检查长度要求
  const errors = [];
  if (password.length === 0) {
    errors.push('密码不能为空');
  }
  if (password.length > 0 && password.length < 6) {
    errors.push('密码至少需要6个字符');
  }
  if (password.length > 100) {
    errors.push('密码不能超过100个字符');
  }
  return {
    isValid: errors.length === 0,
    errors
  };
};

const LoginForm = ({ onLoginSuccess, onNavigateToRegister }) => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // 检查是否已登录，如果是则提示用户
    const authInfo = cookieManager.getAuthInfo();
    if (authInfo.isLoggedIn) {
      // 可以显示一个提示，询问用户是否要继续登录或返回主页
      console.log('用户已登录，可以选择返回主页');
    }
  }, []);
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  
  useEffect(() => {
    // 清除之前的锁定状态
    cookieManager.deleteCookie('loginLockoutTime');
    cookieManager.deleteCookie('loginAttempts');
  }, []);
  
  const handleInputChange = (field, value) => {
    // 输入时进行实时清理
    const sanitizedValue = sanitizeInput(value);
    
    setFormData(prev => ({
      ...prev,
      [field]: sanitizedValue
    }));
    
    // 清除对应字段的错误
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // 用户名验证
    if (!formData.username) {
      newErrors.username = '请输入用户名';
    } else {
      const usernameValidation = validateUsername(formData.username);
      if (!usernameValidation.isValid && usernameValidation.errors && usernameValidation.errors.length > 0) {
        newErrors.username = usernameValidation.errors[0];
      }
    }
    
    // 密码验证
    if (!formData.password) {
      newErrors.password = '请输入密码';
    } else {
      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.isValid && passwordValidation.errors && passwordValidation.errors.length > 0) {
        newErrors.password = passwordValidation.errors[0];
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 表单验证
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // 重要：包含Cookie
        body: JSON.stringify({
          username: formData.username,
          password: formData.password
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('登录成功:', result);
        
        // 后端通过 HttpOnly lb_session Cookie 维护真实登录态，前端只保存展示信息
        const username = result.user?.username || formData.username;
        cookieManager.setAuthInfo(null, username, true);

        console.log('认证信息已设置:', {
          username,
          isLoggedIn: true
        });
        
        // 调用父组件的登录成功回调
        onLoginSuccess();

        // 若来源页带了 next（被登录拦截前的目标地址），登录后跳回去；否则回首页
        const next = new URLSearchParams(window.location.search).get('next');
        // 只接受站内相对路径，防止被构造成跳转到外部站点
        navigate(next && next.startsWith('/') && !next.startsWith('//') ? next : '/');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || '登录失败');
      }
      
    } catch (error) {
      console.error('登录失败:', error);
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setErrors({
          general: '网络连接失败，请检查服务器是否正常运行'
        });
      } else {
        setErrors({
          general: error.message || '登录失败，请检查用户名和密码'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleNavigateToRegister = () => {
    navigate('/auth/register');
  };
  

  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* 登录标题 */}
        <div className="mb-8 text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">用户登录</h1>
          <p className="text-gray-600">请输入您的用户名和密码</p>
        </div>
        
        {/* 登录表单 */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 邮箱输入 */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                用户名
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  ref={emailRef}
                  id="username"
                  type="text"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    errors.username ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="请输入用户名"
                  disabled={isLoading}
                  autoComplete="username"
                  maxLength={100}
                />
                {formData.username && !errors.username && validateUsername(formData.username) && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  </div>
                )}
              </div>
              {errors.username && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.username}
                </p>
              )}
            </div>
            
            {/* 密码输入 */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                密码
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  ref={passwordRef}
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className={`block w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    errors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="请输入密码"
                  disabled={isLoading}
                  autoComplete="current-password"
                  maxLength={128}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.password}
                </p>
              )}
            </div>
            

            
            {/* 错误信息 */}
            {errors.general && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                  <p className="text-sm text-red-800">{errors.general}</p>
                </div>
              </div>
            )}


            {/* 忘记密码链接 */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => navigate('/auth/reset-password')}
                className="text-sm text-blue-600 hover:text-blue-500 transition-colors"
              >
                忘记密码？
              </button>
            </div>


            {/* 登录按钮 */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg text-sm font-medium text-white transition-all duration-200 ${
                isLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-lg hover:shadow-xl'
              }`}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  登录中...
                </div>
              ) : (
                '登录'
              )}
            </button>
          </form>

          {/* 注册链接 */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              还没有账户？{' '}
              <button
                type="button"
                onClick={handleNavigateToRegister}
                className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
              >
                立即注册
              </button>
            </p>
          </div>
        </div>


      </div>

      {/* 页脚 */}
      <Footer variant="simple" showCopyright={false} showFiling={false} />
    </div>
  );
};

export default LoginForm;